<template>
  <User_Access_Table_V2 v-if="useModernUserList" />

  <!-- Table Container -->
  <div v-else class="bg-white rounded-lg border border-gray-200 overflow-hidden">
    <ComponentHeader title="Users">
      <div class="flex gap-2">
        <ExportMenu :disabled="!rowData.length" :exports="[{ type: 'excel', action: exportToExcel }]" />
        <button
          v-if="canCreateUser"
          class="btn-primary-sm"
          @click="showCreateUserForm = true"
        >
          Create User
        </button>
      </div>
    </ComponentHeader>
    <div class="relative" style="height: 900px">
      <AgGridVue
        :theme="standardListTheme"
        style="width: 100%; height: 100%"
        :column-defs="columnDefs"
        :row-data="processedRowData"
        :default-col-def="defaultColDef"
        :grid-options="gridOptions"
        :loading="loading"
        @grid-ready="onGridReady"
      />
    </div>

    <!-- Create User Form Modal -->
    <Create_User_Form
      v-if="showCreateUserForm"
      @close="showCreateUserForm = false"
      @user-created="handleUserCreated"
    />
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue3';
import { listUsers, updateUser } from '@/services/api/adminUsers';
import { standardListTheme } from '@/utils/common';
import CC_Badge_List from '@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_Badge_List.vue';
import CC_Dropdown_Menu from '@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_Dropdown_Menu.vue';
import CC_Router_Link from '@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_Router_Link.vue';
import { mapGetters } from 'vuex';
import { FEATURES } from '@/constants/features';
import { canAccessFeature } from '@/utils/access';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import Create_User_Form from '@/components/forms/Create_User_Form.vue';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import { exportToExcel } from '@/utils/exportArraytoXlsx.js';
import User_Access_Table_V2 from '@/components/Lists/User_Access_Table_V2.vue';

export default {
  components: {
    AgGridVue,
    ComponentHeader,
    Create_User_Form,
    ExportMenu,
    User_Access_Table_V2,
  },
  data() {
    return {
      // Set false to use the legacy AG Grid table.
      useModernUserList: true,
      gridApi: null,
      gridOptions: {
        animateRows: true,
        statusBar: {
          statusPanels: [
            { statusPanel: 'agTotalAndFilteredRowCountComponent' },
          ],
        },
      },
      loading: true,
      defaultColDef: {
        flex: 1,
        autoHeight: true,
        sortable: true,
        filter: true,
        resizable: true,
        wrapText: true,
        cellClass: 'cell-theme-left-aligned',
      },
      rowData: [],
      showCreateUserForm: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    ...mapGetters('tenant', ['current_tenant_slug']),
    ...mapGetters('featureConfig', ['roles']),
    standardListTheme() {
      return standardListTheme;
    },
    canCreateUser() {
      return canAccessFeature(FEATURES.ACTION_USER_CREATE);
    },
    canToggleActive() {
      return canAccessFeature(FEATURES.ACTION_USER_CREATE, { superuserOverride: false });
    },
    processedRowData() {
      return this.rowData.map(user => ({
        ...user,
        pretty_access_roles: user.access_roles.map(roleId => {
          const found = this.roles.find(r => r.role_id === roleId);
          return found ? found.display_name : roleId;
        }),
      }));
    },
    columnDefs() {
      return [
        {
          headerName: 'Last Name',
          field: 'last_name',
          sort: 'asc',
          cellRenderer: CC_Router_Link,
          cellRendererParams: {
            to: params => `/admin/user/${params.data.id}`,
          },
        },
        {
          headerName: 'First Name',
          field: 'first_name',
          cellRenderer: CC_Router_Link,
          cellRendererParams: {
            to: params => `/admin/user/${params.data.id}`,
          },
        },
        { headerName: 'Email', field: 'email', flex: 2, minWidth: 200 },
        {
          headerName: 'Kairon Standard',
          children: [
            {
              headerName: 'Roles',
              field: 'pretty_access_roles',
              valueGetter: params =>
                params.data.pretty_access_roles.map(name => ({ name })),
              cellRenderer: CC_Badge_List,
              minWidth: 180,
              filterValueGetter: params => params.data.pretty_access_roles,
            },
          ],
        },
        {
          headerName: 'Portal',
          children: [
            {
              headerName: 'Task Types',
              field: 'portal_roles',
              cellRenderer: CC_Badge_List,
              cellRendererParams: {
                badgeTextVarName: 'role_name',
              },
              minWidth: 180,
              filterValueGetter: params =>
                params.data.portal_roles.map(r => r.role_name),
            },
            {
              headerName: '# Entities',
              field: 'entity_access_count',
              minWidth: 120,
              maxWidth: 160,
              cellClass: 'text-center',
            },
          ],
        },
        {
          headerName: 'Enabled?',
          field: 'is_active',
          minWidth: 50,
          maxWidth: 130,
          cellRenderer: params => (params.value ? '✓' : ''),
          cellClass: 'flex justify-center',
        },
        {
          cellRenderer: CC_Dropdown_Menu,
          cellRendererParams: params => {
            const menuItems = [
              {
                label: 'Edit',
                action: () => this.$router.push(`/admin/user/${params.data.id}`),
              },
            ];
            if (
              this.canToggleActive &&
              params.data.id !== this.current_user?.id
            ) {
              menuItems.push({
                label: params.data.is_active ? 'Block Access' : 'Permit Access',
                action: () => this.toggleUserActive(params.data),
              });
            }
            return { menuItems };
          },
          maxWidth: 80,
          cellClass: 'flex justify-center',
          sortable: false,
          suppressHeaderMenuButton: true,
          suppressHeaderFilterButton: true,
        },
      ];
    },
  },
  watch: {
    current_tenant_slug() {
      this.$store.dispatch('featureConfig/fetchConfig');
    },
  },
  mounted() {
    // featureConfig.roles is needed to resolve role_id → display_name below.
    this.$store.dispatch('featureConfig/fetchConfig');
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
  },
  methods: {
    async onGridReady(params) {
      this.gridApi = params.api;

      try {
        const users = await listUsers();
        this.rowData = users;
        this.loading = false;
        params.api.sizeColumnsToFit();
        window.addEventListener('resize', this.onWindowResize);
      } catch (error) {
        console.error('Error fetching data:', error);
        this.loading = false;
      }
    },
    onWindowResize() {
      if (this.gridApi) {
        this.gridApi.sizeColumnsToFit();
      }
    },
    exportToExcel() {
      const rows = this.processedRowData.map((user) => ({
        'Last Name': user.last_name || '',
        'First Name': user.first_name || '',
        'Email': user.email || '',
        'Roles': user.pretty_access_roles.join(', '),
        'Portal Task Types': (user.portal_roles || []).map((r) => r.role_name).join(', '),
        '# Entities': user.entity_access_count,
        'Enabled': user.is_active ? 'Yes' : 'No',
      }));

      const today = new Date().toISOString().split('T')[0];
      exportToExcel(rows, `users_${today}.xlsx`, {
        sheetName: 'Users',
        columnWidths: {
          'Last Name': 140,
          'First Name': 120,
          'Email': 250,
          'Roles': 200,
          'Portal Task Types': 180,
          '# Entities': 90,
          'Enabled': 80,
        },
      });
    },
    async handleUserCreated() {
      try {
        this.rowData = await listUsers();
      } catch (error) {
        console.error('Error refreshing user data:', error);
      }
    },
    async toggleUserActive(user) {
      if (this.loading) return;
      this.loading = true;
      const newIsActive = !user.is_active;
      try {
        await updateUser(user.id, { is_active: newIsActive });
        const idx = this.rowData.findIndex(u => u.id === user.id);
        if (idx !== -1) this.rowData[idx] = { ...this.rowData[idx], is_active: newIsActive };
      } catch (error) {
        console.error('Failed to update user status.', error);
      }
      this.loading = false;
    },
  },
};
</script>
